import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import { useJumboTheme } from "@jumbo/hooks";
import Div from "@jumbo/shared/Div";
import { Button, Typography, Snackbar, Alert, AlertTitle } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import merge from "lodash/merge";
import mapValues from "lodash/mapValues";

import { Form, Formik } from "formik";
// import useJumboAuth from "@jumbo/hooks/useJumboAuth";

import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";

import LocationsField, {
  getLocationsFieldSchema,
  getLocationsFieldValidationSchema,
} from "../customers/dsok/fields/LocationsField";
import * as Yup from "yup";
import DevicesField, {
  getDevicesFieldSchema,
  getDevicesFieldValidationSchema,
} from "../customers/dsok/fields/DevicesField";
import {
  SETTINGS_FIELD_SCHEMA,
  getSettingsFieldValidationSchema,
} from "../customers/dsok/fields/SettingsField";
import MaintenanceField, {
  getMaintenanceFieldSchema,
  getMaintenanceFieldValidationSchema,
} from "../customers/dsok/fields/MaintenanceField";
import {
  ORDER_MANAGEMENT_FIELD_SCHEMA,
  getOrderManagementFieldValidationSchema,
} from "../customers/dsok/fields/OrderManagementField";
import { mergeDeep } from "app/utils/objectHelpers";
import {
  UI_SETTINGS_FIELD_SCHEMA,
  getUISettingsFieldValidationSchema,
} from "../customers/dsok/fields/UISettingsField";
// import TokenIcon from "@mui/icons-material/Token";
// import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
// import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { DSOK_DEVICE_SERVER_PORT_START } from "app/utils/constants/settings";

import {
  getPrinterSettingsFieldSchema,
  getPrinterSettingsFieldValidationSchema,
} from "../customers/dsok/fields/PrinterSettingsFields";
import usePartnerAuth from "@jumbo/hooks/usePartnerAuth";
import { getCustomer, updateCustomer } from "app/services/api/partner";
import { lang } from "moment";
import activeLocale from "app/services/partner/activeLocale";
import InvalidTokenMessage from "./invalidTokenMessage";
import DsokCustomerField, {
  DSOK_CUSTOMER_FIELD_SCHEMA,
  getDsokCustomerFieldValidationSchema,
} from "../customers/dsok/fields/DsokCustomerField";
import manageToken from "./ManageToken";

const Admin = () => {
  // const { url } = useParams();
  const { t } = useTranslation();
  // const { i18n } = useTranslation();
  const { theme } = useJumboTheme();
  // const { getAuthUser } = useJumboAuth();
  // const { customer: authUser } = getAuthUser() || {};
  const [customer, setCustomer] = useState();
  const [unsaved] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [envType, setEnvType] = useState();
  const { enqueueSnackbar } = useSnackbar();
  const formRef = useRef();
  const [expanded, setExpanded] = useState(false);
  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };
  // const [openError, setOpenError] = useState(false);
  const [searchParams] = useSearchParams();
  const { validate, user } = usePartnerAuth();
  const token = manageToken(searchParams);
  // eslint-disable-next-line no-unused-vars
  const lang = searchParams.get("hl");
  const [validated, setValidated] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isValidating, setIsValidating] = useState(false);
  const customerUrl = customer?.url ?? "";
  const isAdmin = user?.role === "admin";
  const isPartner = false;
  // const { showDialog, hideDialog } = useJumboDialog();
  // eslint-disable-next-line no-unused-vars
  const [tokenChecked, setTokenChecked] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    activeLocale(lang);
    if (token) {
      setTokenChecked(true);
      setIsValidating(true);
      validate(token)
        .then((res) => {
          setValidated(true);
          try {
            const url = new URL(window.location.href);
            if (url.searchParams.has("token")) {
              url.searchParams.delete("token");
              window.history.replaceState({}, document.title, url.toString());
            }
          } catch (e) {}
          setEnvType("prod");
          if (
            res?.iss === "smolkoland.ikelp.com" ||
            res?.iss === "skusit.ikelp.com" ||
            res?.iss === "intesso.ikelp.com"
          ) {
            setEnvType("staging");
          }
        })
        .catch((error) => {
          setValidated(false);
          console.log("Token validation failed", error);
        })
        .finally(() => setIsValidating(false));
    }
  }, [token, lang]);

  const validationSchema = Yup.object().shape({
    customer: Yup.object().shape({
      ...getDsokCustomerFieldValidationSchema(t),
      locations: Yup.lazy((lazyObj) =>
        Yup.object(
          mapValues(lazyObj, () =>
            Yup.object(getLocationsFieldValidationSchema(t)),
          ),
        ),
      ),
      devices: Yup.lazy((lazyObj) =>
        Yup.object(
          mapValues(lazyObj, () =>
            Yup.object(getDevicesFieldValidationSchema(t)),
          ),
        ),
      ),
    }),
    settings: Yup.object().shape(getSettingsFieldValidationSchema(t)),
    maintenance: Yup.object().shape(getMaintenanceFieldValidationSchema(t)),
    printerSettings: Yup.object().shape(
      getPrinterSettingsFieldValidationSchema(t),
    ),
    orderManagement: Yup.object().shape(
      getOrderManagementFieldValidationSchema(t),
    ),
    uiSettings: Yup.object().shape(getUISettingsFieldValidationSchema(t)),
  });

  const loadCustomer = React.useCallback(async () => {
    if (!envType) return;
    await getCustomer(token, envType).then((customer) => setCustomer(customer));
  }, [envType, token]);

  const handleUpdateCustomer = async (settings) => {
    Object.keys(settings?.customer.locations || {}).forEach(
      (key) => (settings.customer.locations[key].id = +key),
    );
    Object.keys(settings?.customer?.devices || {}).forEach((key) => {
      settings.customer.devices[key].id = +key;
      const posPort = DSOK_DEVICE_SERVER_PORT_START + (+key - 1) * 16;
      const ekasaPort = posPort + 1;
      const generalServerPort = ekasaPort + 1;
      // console.log('device', key, posPort, ekasaPort, generalServerPort)
      settings.customer.devices[key].pos.serverPort = posPort;
      settings.customer.devices[key].ekasa.serverPort = ekasaPort;
      settings.customer.devices[key].generalServer.serverPort =
        generalServerPort;
    });
    if (settings?.customer) {
      const { enableDynamicComponents: _removed, ...customerRest } = settings.customer;
      settings = { ...settings, customer: customerRest };
    }
    const dsok = { ...customer.dsokDetails, ...settings };
    setUpdating(true);
    try {
      await updateCustomer({ customerUrl, ...dsok }, token);
      await loadCustomer();
      const message = `${t("buttons.update")} ${t(
        "notifications.wasSuccessful",
      )}`;
      enqueueSnackbar(message, {
        variant: "success",
      });
    } catch (error) {
      const message = `${t("buttons.update")} ${t("notifications.wasFailed")}`;
      enqueueSnackbar(message, {
        variant: "error",
      });
      setUpdating(false);
    }
    setUpdating(false);
  };

  const getInitialValues = () => {
    const mergedCustomer = mergeDeep(
      DSOK_CUSTOMER_FIELD_SCHEMA,
      customer.dsokSettings?.customer,
    );
    delete mergedCustomer.enableDynamicComponents;
    return {
    customer: {
      ...mergedCustomer,
      locations: merge(
        getLocationsFieldSchema(customer.dsokSettings?.customer?.locations),
        customer?.dsokSettings?.customer?.locations || {},
      ),
      devices: merge(
        getDevicesFieldSchema(customer.dsokSettings?.customer?.devices),
        customer?.dsokSettings?.customer?.devices || {},
      ),
    },
    settings: merge(SETTINGS_FIELD_SCHEMA, customer.dsokSettings?.settings),
    maintenance: merge(
      getMaintenanceFieldSchema(
        Object.keys(customer.dsokSettings?.maintenance?.message || {}),
      ),
      customer.dsokSettings?.maintenance,
    ),
    printerSettings: merge(
      getPrinterSettingsFieldSchema(
        Object.keys(customer.dsokSettings?.printerSettings?.message || {}),
      ),
      customer.dsokSettings?.printerSettings,
    ),
    orderManagement: merge(
      ORDER_MANAGEMENT_FIELD_SCHEMA,
      customer.dsokSettings?.orderManagement,
    ),
    uiSettings: merge(
      UI_SETTINGS_FIELD_SCHEMA,
      customer.dsokSettings?.uiSettings,
    ),
  };
  };

  useEffect(() => {
    loadCustomer();
  }, [envType, loadCustomer]);

  const initialValues = customer && getInitialValues();
  if (validated === false || !customer) {
    return InvalidTokenMessage();
  }
  if (customer === undefined) return <div>Invalid token...</div>;
  return (
    <>
      {/* <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        {isAdmin && (
          <Link to="/customers">{t("sidebar.menuItem.customers")}</Link>
        )}
        <Typography color="text.primary">
          {`${customer?.companyName || ""}`}
        </Typography>
      </Breadcrumbs> */}
      <Div
        sx={{
          display: "flex",
        }}
      >
        <JumboCardQuick
          title={
            <Typography
              component={"div"}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: 29,
                [theme.breakpoints.down("md")]: {
                  flexWrap: "wrap",
                },
              }}
            >
              <Typography
                variant={"h4"}
                mb={0}
                sx={{
                  minWidth: 245,
                  [theme.breakpoints.down("md")]: {
                    minWidth: "100%",
                    marginBottom: 2,
                  },
                }}
              >
                {`${customer?.companyName || ""}`}
              </Typography>
              <Div sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    boxShadow: "none",
                    height: "38px",
                  }}
                  startIcon={<BrowserUpdatedIcon />}
                  disabled={updating}
                  onClick={() => {
                    if (formRef.current) {
                      if (!formRef.current.isValid) {
                        const message = `${t("messages.checkRequiredFields")}`;
                        enqueueSnackbar(message, {
                          variant: "error",
                        });
                        return;
                      }
                      formRef.current.handleSubmit();
                    }
                  }}
                >
                  {t("buttons.update")}
                </Button>
              </Div>
            </Typography>
          }
          headerSx={{
            borderBottom: 1,
            borderBottomColor: "divider",
            "& .MuiCardHeader-action": {
              my: -0.75,
            },
          }}
          sx={{
            flex: 1,
            mr: 2,
          }}
          wrapperSx={{
            p: 1,
            "&:last-child": {
              pb: 2,
            },
            "& .MuiCollapse-entered:last-child": {
              "& .MuiListItemButton-root": {
                borderBottom: 0,
                borderBottomColor: "transparent",
              },
            },
          }}
        >
          {customer && (
            <Formik
              innerRef={formRef}
              validateOnChange={true}
              validationSchema={validationSchema}
              initialValues={initialValues}
              onSubmit={async (data, { setSubmitting }) => {
                setSubmitting(true);
                await handleUpdateCustomer(data);
                setSubmitting(false);
              }}
            >
              {({ isSubmitting, values, setFieldValue }) => (
                <Form style={{ textAlign: "left" }} noValidate>
                  {isAdmin && (
                    <DsokCustomerField
                      handleChange={handleChange}
                      expanded={expanded}
                      isPartnerAdmin={isAdmin}
                    />
                  )}
                  {(isAdmin || isPartner) && (
                    <>
                      <LocationsField
                        handleChange={handleChange}
                        expanded={expanded}
                        isPartner={true}
                        isPartnerAdmin={true}
                        customer={customer}
                      />
                    </>
                  )}
                  <DevicesField
                    handleChange={handleChange}
                    expanded={expanded}
                    isAdmin={false}
                    customerUrl={customerUrl}
                    isPartner={true}
                    isPartnerAdmin={true}
                    token={token}
                  />
                  {(isAdmin || isPartner) && (
                    <>
                      <MaintenanceField
                        handleChange={handleChange}
                        expanded={expanded}
                      />
                    </>
                  )}
                </Form>
              )}
            </Formik>
          )}
        </JumboCardQuick>
      </Div>
      <Snackbar
        open={unsaved}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="info">
          <AlertTitle>{t("messages.unsavedChanges.title")}</AlertTitle>
          {t("messages.unsavedChanges.description")}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Admin;
